import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export const Stars = ({ value, onChange, size = "h-6 w-6", testId = "rating-star" }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} disabled={!onChange} data-testid={`${testId}-${n}`} onClick={() => onChange?.(n)} onMouseEnter={() => onChange && setHover(n)} className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}>
          <Star className={`${size} ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>))}
    </div>
  );
};

export function TicketRating({ ticket, meta, onRated }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (!["risolto", "chiuso"].includes(ticket.status)) return null;
  if (ticket.rating) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="ticket-rating-done">
        <Stars value={ticket.rating.stars} size="h-4 w-4" />
        <span className="text-slate-700">Valutazione {ticket.rating.stars}/5{ticket.rating.comment && <span className="text-slate-500"> · "{ticket.rating.comment}"</span>}</span>
      </div>
    );
  }
  if (meta.support) return <p className="text-xs text-slate-400" data-testid="ticket-rating-pending">In attesa della valutazione del cliente.</p>;
  const send = async () => {
    setBusy(true);
    try { const r = await api.put(`/tickets/${ticket.id}/rating`, { stars, comment }); toast.success("Grazie per la valutazione!"); onRated(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="ticket-rating-form">
      <div className="text-sm font-medium text-slate-800">Come valuti l'assistenza ricevuta?</div>
      <Stars value={stars} onChange={setStars} />
      <Textarea data-testid="ticket-rating-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Un commento (facoltativo)" className="bg-white" />
      <Button size="sm" data-testid="ticket-rating-send" className="bg-amber-500 hover:bg-amber-600" disabled={!stars || busy} onClick={send}>{busy ? "Invio..." : "Invia valutazione"}</Button>
    </div>
  );
}

export function RatingSummary() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tickets-stats").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.count) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="rating-summary">
      <div className="flex items-center gap-2"><span className="font-heading text-2xl font-semibold text-slate-900">{d.average.toFixed(1).replace(".", ",")}</span><Stars value={Math.round(d.average)} size="h-4 w-4" /></div>
      <span className="text-xs text-slate-500">{d.count} valutazion{d.count > 1 ? "i" : "e"}</span>
      <div className="flex items-center gap-2 text-[11px] text-slate-500">{[5, 4, 3, 2, 1].map((n) => <span key={n} data-testid={`rating-dist-${n}`}>{n}★ <b className="text-slate-700">{d.distribution[n]}</b></span>)}</div>
    </div>
  );
}
